import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CurrentBookingDetails from './components/CurrentBookingDetails';
import ModificationPanel from './components/ModificationPanel';
import PriceComparisonPanel from './components/PriceComparisonPanel';
import ChangeSummary from './components/ChangeSummary';
import ConfirmationModal from './components/ConfirmationModal';
import { useAuth } from '../../contexts/AuthContext';

const BookingModification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [booking, setBooking] = useState(location?.state?.booking || null);
  const [modifications, setModifications] = useState({
    dates: null,
    accommodation: null,
    activities: { added: [], removed: [] }
  });
  const [originalPricing, setOriginalPricing] = useState(null);
  const [updatedPricing, setUpdatedPricing] = useState(null);
  const [changeFees, setChangeFees] = useState(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [modificationHistory, setModificationHistory] = useState([]);

  useEffect(() => {
    if (!booking) {
      // If no booking passed, redirect to bookings dashboard
      navigate('/bookings');
      return;
    }

    // Initialize original pricing
    const baseAmount = booking?.amount * 0.8;
    const taxesAndFees = booking?.amount * 0.12;
    const serviceFee = booking?.amount * 0.08;
    
    setOriginalPricing({
      baseAmount,
      taxesAndFees,
      serviceFee,
      total: booking?.amount
    });

    setUpdatedPricing({
      baseAmount,
      taxesAndFees,
      serviceFee,
      total: booking?.amount
    });
  }, [booking, navigate]);

  const handleDateChange = (newDates) => {
    setModifications(prev => ({
      ...prev,
      dates: newDates
    }));

    // Recalculate pricing based on date changes
    recalculatePricing({ ...modifications, dates: newDates });
    validateChanges({ ...modifications, dates: newDates });
  };

  const handleAccommodationChange = (newAccommodation) => {
    setModifications(prev => ({
      ...prev,
      accommodation: newAccommodation
    }));

    // Recalculate pricing
    recalculatePricing({ ...modifications, accommodation: newAccommodation });
    validateChanges({ ...modifications, accommodation: newAccommodation });
  };

  const handleActivityChange = (activityChanges) => {
    setModifications(prev => ({
      ...prev,
      activities: activityChanges
    }));

    // Recalculate pricing
    recalculatePricing({ ...modifications, activities: activityChanges });
    validateChanges({ ...modifications, activities: activityChanges });
  };

  const recalculatePricing = (currentModifications) => {
    let newBaseAmount = originalPricing?.baseAmount || 0;
    let fees = 0;

    // Date change fee
    if (currentModifications?.dates) {
      fees += 500; // Flat fee for date changes
      
      // Adjust base amount if dates extend the stay
      const originalNights = calculateNights(booking?.checkIn, booking?.checkOut);
      const newNights = calculateNights(
        currentModifications?.dates?.checkIn,
        currentModifications?.dates?.checkOut
      );
      
      if (newNights > originalNights) {
        const pricePerNight = originalPricing?.baseAmount / originalNights;
        newBaseAmount = pricePerNight * newNights;
      }
    }

    // Accommodation change fee
    if (currentModifications?.accommodation) {
      fees += 300;
      newBaseAmount = currentModifications?.accommodation?.price || newBaseAmount;
    }

    // Activity changes
    if (currentModifications?.activities?.added?.length > 0) {
      const activityCosts = currentModifications?.activities?.added?.reduce(
        (sum, activity) => sum + (activity?.price || 0),
        0
      );
      newBaseAmount += activityCosts;
    }

    if (currentModifications?.activities?.removed?.length > 0) {
      fees += currentModifications?.activities?.removed?.length * 100; // Cancellation fee per activity
    }

    setChangeFees(fees);

    const newTaxesAndFees = newBaseAmount * 0.12;
    const newServiceFee = newBaseAmount * 0.08;
    const newTotal = newBaseAmount + newTaxesAndFees + newServiceFee + fees;

    setUpdatedPricing({
      baseAmount: newBaseAmount,
      taxesAndFees: newTaxesAndFees,
      serviceFee: newServiceFee,
      total: newTotal
    });
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const validateChanges = (currentModifications) => {
    const errors = [];

    // Date validation
    if (currentModifications?.dates) {
      const checkIn = new Date(currentModifications?.dates?.checkIn);
      const checkOut = new Date(currentModifications?.dates?.checkOut);
      const today = new Date();

      if (checkIn < today) {
        errors?.push('Check-in date cannot be in the past');
      }

      if (checkOut <= checkIn) {
        errors?.push('Check-out date must be after check-in date');
      }

      // Check if within cancellation policy window
      const originalCheckIn = new Date(booking?.checkIn);
      const daysUntilCheckIn = Math.ceil((originalCheckIn - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilCheckIn < 3) {
        errors?.push('Cannot modify booking within 3 days of check-in');
      }
    }

    // Accommodation validation
    if (currentModifications?.accommodation) {
      if (!currentModifications?.accommodation?.available) {
        errors?.push('Selected accommodation is not available for your dates');
      }
    }

    setValidationErrors(errors);
  };

  const hasModifications = () => {
    return (
      modifications?.dates ||
      modifications?.accommodation ||
      modifications?.activities?.added?.length > 0 ||
      modifications?.activities?.removed?.length > 0
    );
  };

  const handleResetChanges = () => {
    setModifications({
      dates: null,
      accommodation: null,
      activities: { added: [], removed: [] }
    });
    setUpdatedPricing(originalPricing);
    setChangeFees(0);
    setValidationErrors([]);
  };

  const handleConfirmModification = () => {
    if (validationErrors?.length > 0) {
      return;
    }

    setIsConfirmationModalOpen(true);
  };

  const handleFinalConfirmation = async () => {
    // Save modification to history
    const modificationRecord = {
      timestamp: new Date()?.toISOString(),
      changes: modifications,
      oldPricing: originalPricing,
      newPricing: updatedPricing,
      fees: changeFees
    };

    setModificationHistory(prev => [...prev, modificationRecord]);

    // In a real app, this would update the booking in the database
    // For now, navigate back to bookings with success message
    setIsConfirmationModalOpen(false);
    navigate('/bookings', { 
      state: { 
        message: 'Booking modified successfully',
        updatedBooking: {
          ...booking,
          ...(modifications?.dates && {
            checkIn: modifications?.dates?.checkIn,
            checkOut: modifications?.dates?.checkOut
          }),
          amount: updatedPricing?.total
        }
      } 
    });
  };

  if (!booking) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/bookings')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Back to Bookings</span>
            </button>
            <h1 className="text-4xl font-bold text-foreground mb-2">Modify Booking</h1>
            <p className="text-muted-foreground">Confirmation #{booking?.confirmationNumber}</p>
          </div>

          {/* Validation Errors */}
          {validationErrors?.length > 0 && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-error mb-2">Validation Errors</h3>
                  <ul className="space-y-1">
                    {validationErrors?.map((error, index) => (
                      <li key={index} className="text-sm text-error">
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Three-Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Left Panel - Current Booking Details */}
            <div className="lg:col-span-1">
              <CurrentBookingDetails booking={booking} />
            </div>

            {/* Center Panel - Modification Controls */}
            <div className="lg:col-span-1">
              <ModificationPanel
                booking={booking}
                modifications={modifications}
                onDateChange={handleDateChange}
                onAccommodationChange={handleAccommodationChange}
                onActivityChange={handleActivityChange}
              />
            </div>

            {/* Right Panel - Price Comparison */}
            <div className="lg:col-span-1">
              <PriceComparisonPanel
                originalPricing={originalPricing}
                updatedPricing={updatedPricing}
                changeFees={changeFees}
              />
            </div>
          </div>

          {/* Change Summary */}
          {hasModifications() && (
            <ChangeSummary
              modifications={modifications}
              booking={booking}
              changeFees={changeFees}
            />
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              variant="outline"
              onClick={handleResetChanges}
              disabled={!hasModifications()}
            >
              <Icon name="RotateCcw" size={18} />
              Reset Changes
            </Button>
            <Button
              onClick={handleConfirmModification}
              disabled={!hasModifications() || validationErrors?.length > 0}
              className="bg-primary hover:bg-primary/90"
            >
              <Icon name="Check" size={18} />
              Confirm Modifications
            </Button>
          </div>
        </div>
      </main>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleFinalConfirmation}
        booking={booking}
        modifications={modifications}
        updatedPricing={updatedPricing}
        changeFees={changeFees}
      />
    </div>
  );
};

export default BookingModification;